import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import ModalDelegate from './modalDelegate';

interface SendOutEmailsFields extends DefaultFormFields {
  primaryEmail: FormControl<string>;
  message: FormControl<string>;
  [key: string]: FormControl<string>;
}

interface Props {
  onSubmit: (recipientEmails: string[], message?: string) => Promise<void>;
}

export class SendOutEmailsModalStore extends ModalDelegate {
  private _form: FormController<SendOutEmailsFields>;
  private _lastCC: number = 0;
  private _onSubmit;

  public get form(): FormController<SendOutEmailsFields> {
    return this._form;
  }

  public get ccFields(): string[] {
    return Object.keys(this._form.fields).filter((fieldName) => fieldName.startsWith('ccEmail'));
  }

  private async _submitForm(): Promise<void> {
    await runInAction(async () => {
      const { primaryEmail, message, ...ccFields } = this.form.getFormDataObject();
      await this._onSubmit([primaryEmail, ...Object.values(ccFields)], message);
    });
    this.close();
  }

  public get areAllEmailsUnique(): boolean {
    const emails = [
      this.form.fields.primaryEmail.formattedValue,
      ...Object.entries(this._form.fields)
        .filter(([key]) => key.startsWith('ccEmail'))
        .map(([_, control]) => control.formattedValue)
        .filter((value) => value.trim() !== ''), // Remove empty fields
    ];
    return new Set(emails).size === emails.length;
  }

  public addCc(): void {
    this._lastCC += 1;
    const newFieldKey = `ccEmail${this._lastCC}`;
    this.form.addFormField(
      newFieldKey,
      new FormControl<string>('', {
        validations: ['isEmailValid'],
        trim: true,
      }),
    );
  }

  public clearModalData(): void {
    this.ccFields.forEach((field) => {
      this._form.removeFormField(field);
    });
    this._form.resetForm();
  }

  constructor({ onSubmit }: Props) {
    super();
    this._onSubmit = onSubmit;
    this._form = new FormController(
      {
        primaryEmail: new FormControl<string>('', { validations: ['isEmailValid'], trim: true }),
        message: new FormControl<string>('', { optional: true, trim: true }),
      },
      { onSubmit: this._submitForm.bind(this) },
    );

    makeObservable<SendOutEmailsModalStore, '_form' | '_lastCC' | '_onSubmit' | '_submitForm'>(this, {
      _form: observable,
      _lastCC: observable,

      form: computed,
      ccFields: computed,
      areAllEmailsUnique: computed,

      _onSubmit: action.bound,
      _submitForm: action.bound,
      addCc: action.bound,
      clearModalData: action.bound,
    });
  }
}
